import { Pressable, StyleSheet, ScrollView, Text, TextInput, useColorScheme, Alert } from "react-native";
import AsyncStorage from "@react-native-async-storage/async-storage";
import React, { useEffect, useState } from "react";
import { View } from "../components/Themed";
import { useQuery, gql, useMutation } from "@apollo/client";
import Config from "../constants/Config";
import { v4 as uuidv4 } from "uuid";

const CHECKPOINTS_QUERY = gql`
  query getCheckpoints($token: String!) {
    inventoryCheckpoints(token: $token) {
      id
      date
      operatorId
      notes
      createdAt
      items {
        inventoryItemId
        expectedQuantity
        actualQuantity
        inventoryItem {
          id
          name
          unit
        }
      }
    }
  }
`;

const INVENTORY_QUERY = gql`
  query getInventory($token: String!) {
    inventoryItems(token: $token) {
      id
      name
      unit
      currentQuantity
    }
  }
`;

const CREATE_CHECKPOINT_MUTATION = gql`
  mutation createInventoryCheckpoint(
    $token: String!
    $id: ID!
    $date: String!
    $operatorId: ID
    $notes: String
    $items: [InventoryCheckpointItemInput!]!
  ) {
    createInventoryCheckpoint(
      token: $token
      id: $id
      date: $date
      operatorId: $operatorId
      notes: $notes
      items: $items
    ) {
      id
      date
    }
  }
`;

const DELETE_CHECKPOINT_MUTATION = gql`
  mutation deleteInventoryCheckpoint($token: String!, $id: ID!) {
    deleteInventoryCheckpoint(token: $token, id: $id)
  }
`;

export default function InventoryCheckpointsScreen() {
  const [token, setToken] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [notes, setNotes] = useState("");
  const [checkpointItems, setCheckpointItems] = useState([]);
  const [expandedId, setExpandedId] = useState(null);
  const colorScheme = useColorScheme();
  const isDark = colorScheme === "dark";

  const { loading, error, data, refetch } = useQuery(CHECKPOINTS_QUERY, {
    variables: { token },
    skip: !token,
  });

  const { data: inventoryData } = useQuery(INVENTORY_QUERY, {
    variables: { token },
    skip: !token,
  });

  const [createCheckpoint] = useMutation(CREATE_CHECKPOINT_MUTATION);
  const [deleteCheckpoint] = useMutation(DELETE_CHECKPOINT_MUTATION);

  useEffect(() => {
    async function fetchToken() {
      try {
        const storedToken = await AsyncStorage.getItem("token");
        setToken(storedToken);
      } catch (ex) {}
    }
    fetchToken();
  }, []);

  const startNewCheckpoint = () => {
    const items = (inventoryData?.inventoryItems || []).map(item => ({
      inventoryItemId: item.id,
      name: item.name,
      unit: item.unit || "units",
      expectedQuantity: item.currentQuantity,
      actualQuantity: String(item.currentQuantity),
    }));
    setCheckpointItems(items);
    setNotes("");
    setShowForm(true);
  };

  const updateActualQuantity = (index, value) => {
    const updated = [...checkpointItems];
    updated[index] = { ...updated[index], actualQuantity: value };
    setCheckpointItems(updated);
  };

  const handleSave = async () => {
    if (checkpointItems.length === 0) {
      Alert.alert("Error", "No inventory items to checkpoint");
      return;
    }
    try {
      const operatorId = await AsyncStorage.getItem("operatorId");
      await createCheckpoint({
        variables: {
          token,
          id: uuidv4(),
          date: new Date().toISOString().split("T")[0],
          operatorId,
          notes,
          items: checkpointItems.map(ci => ({
            inventoryItemId: ci.inventoryItemId,
            expectedQuantity: ci.expectedQuantity,
            actualQuantity: parseFloat(ci.actualQuantity) || 0,
          })),
        },
      });
      setShowForm(false);
      refetch();
    } catch (err) {
      Alert.alert("Error", err.message);
    }
  };

  const handleDelete = async (id) => {
    try {
      await deleteCheckpoint({ variables: { token, id } });
      refetch();
    } catch (err) {
      Alert.alert("Error", err.message);
    }
  };

  const textColor = isDark ? "#fff" : "#000";
  const cardBg = isDark ? "#1e1e1e" : "#fff";
  const inputBg = isDark ? "#2a2a2a" : "#f5f5f5";

  return (
    <ScrollView style={{ backgroundColor: isDark ? "#121212" : "#f0f0f0" }}>
      <View style={styles.container}>
        <View style={styles.header}>
          <Text style={[styles.title, { color: textColor }]}>Checkpoints</Text>
          <Pressable
            style={[styles.addButton, { backgroundColor: Config.companyColor || "#007AFF" }]}
            onPress={() => {
              if (showForm) { setShowForm(false); } else { startNewCheckpoint(); }
            }}
          >
            <Text style={styles.addButtonText}>{showForm ? "Cancel" : "+ New Checkpoint"}</Text>
          </Pressable>
        </View>

        {showForm && (
          <View style={[styles.formCard, { backgroundColor: cardBg }]}>
            <Text style={[styles.formTitle, { color: textColor }]}>Physical Count</Text>
            <Text style={[styles.formSubtitle, { color: "#888" }]}>
              Verify actual quantities for each inventory item.
            </Text>
            <TextInput
              style={[styles.input, { backgroundColor: inputBg, color: textColor }]}
              placeholder="Notes (optional)"
              placeholderTextColor="#999"
              value={notes}
              onChangeText={setNotes}
              multiline
            />

            {checkpointItems.length === 0 && (
              <Text style={[styles.emptyText, { color: "#888" }]}>
                No inventory items found. Add items to inventory first.
              </Text>
            )}

            {checkpointItems.map((ci, index) => {
              const diff = (parseFloat(ci.actualQuantity) || 0) - ci.expectedQuantity;
              const hasDiff = diff !== 0;
              return (
                <View key={ci.inventoryItemId} style={[styles.checkItem, { backgroundColor: inputBg }]}>
                  <View style={styles.checkItemHeader}>
                    <Text style={[styles.checkItemName, { color: textColor }]}>{ci.name}</Text>
                    <Text style={[styles.checkItemExpected, { color: "#888" }]}>
                      Expected: {ci.expectedQuantity} {ci.unit}
                    </Text>
                  </View>
                  <View style={styles.checkItemInput}>
                    <Text style={{ color: textColor, marginRight: 8 }}>Actual:</Text>
                    <TextInput
                      style={[styles.quantityInput, { backgroundColor: cardBg, color: textColor }]}
                      value={ci.actualQuantity}
                      onChangeText={(v) => updateActualQuantity(index, v)}
                      keyboardType="numeric"
                    />
                    <Text style={{ color: "#888", marginLeft: 4 }}>{ci.unit}</Text>
                    {hasDiff && (
                      <Text style={[styles.diffText, { color: diff > 0 ? "#28a745" : "#dc3545" }]}>
                        {diff > 0 ? "+" : ""}{diff.toFixed(1)}
                      </Text>
                    )}
                  </View>
                </View>
              );
            })}

            {checkpointItems.length > 0 && (
              <Pressable style={[styles.saveButton, { backgroundColor: "#28a745" }]} onPress={handleSave}>
                <Text style={styles.saveButtonText}>Save Checkpoint</Text>
              </Pressable>
            )}
          </View>
        )}

        {loading && <Text style={{ color: textColor }}>Loading...</Text>}
        {error && <Text style={{ color: "#dc3545" }}>Error: {error.message}</Text>}

        {data?.inventoryCheckpoints?.map((cp) => {
          const isExpanded = expandedId === cp.id;
          const discrepancies = (cp.items || []).filter(ci => ci.expectedQuantity !== ci.actualQuantity);
          return (
            <Pressable key={cp.id} onPress={() => setExpandedId(isExpanded ? null : cp.id)}>
              <View style={[styles.cpCard, { backgroundColor: cardBg }]}>
                <View style={styles.cpHeader}>
                  <View>
                    <Text style={[styles.cpDate, { color: textColor }]}>{cp.date}</Text>
                    <Text style={[styles.cpMeta, { color: "#888" }]}>
                      {cp.items?.length || 0} items checked
                      {discrepancies.length > 0 && ` · ${discrepancies.length} discrepancies`}
                    </Text>
                  </View>
                  <Pressable style={styles.deleteSmall} onPress={() => handleDelete(cp.id)}>
                    <Text style={styles.deleteSmallText}>Delete</Text>
                  </Pressable>
                </View>

                {cp.notes && (
                  <Text style={[styles.cpNotes, { color: "#888" }]}>{cp.notes}</Text>
                )}

                {isExpanded && cp.items?.map((ci, idx) => {
                  const diff = ci.actualQuantity - ci.expectedQuantity;
                  const hasDiff = diff !== 0;
                  return (
                    <View key={idx} style={[styles.cpItem, hasDiff && { borderLeftColor: diff > 0 ? "#28a745" : "#dc3545", borderLeftWidth: 3 }]}>
                      <Text style={[styles.cpItemName, { color: textColor }]}>
                        {ci.inventoryItem?.name || ci.inventoryItemId}
                      </Text>
                      <View style={styles.cpItemRow}>
                        <Text style={{ color: "#888" }}>
                          Expected: {ci.expectedQuantity} {ci.inventoryItem?.unit || ""}
                        </Text>
                        <Text style={{ color: textColor, fontWeight: "600" }}>
                          Actual: {ci.actualQuantity} {ci.inventoryItem?.unit || ""}
                        </Text>
                        {hasDiff && (
                          <Text style={{ color: diff > 0 ? "#28a745" : "#dc3545", fontWeight: "bold" }}>
                            {diff > 0 ? "+" : ""}{diff.toFixed(1)}
                          </Text>
                        )}
                      </View>
                    </View>
                  );
                })}

                {!isExpanded && (
                  <Text style={{ color: "#007AFF", marginTop: 8, fontSize: 13 }}>Tap to expand</Text>
                )}
              </View>
            </Pressable>
          );
        })}

        {data?.inventoryCheckpoints?.length === 0 && (
          <Text style={[styles.emptyText, { color: "#888" }]}>No checkpoints yet. Create one to verify inventory.</Text>
        )}
      </View>
    </ScrollView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    padding: 20,
  },
  header: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 20,
  },
  title: {
    fontSize: 24,
    fontWeight: "bold",
  },
  addButton: {
    paddingHorizontal: 16,
    paddingVertical: 10,
    borderRadius: 8,
  },
  addButtonText: {
    color: "#fff",
    fontWeight: "600",
  },
  formCard: {
    padding: 20,
    borderRadius: 12,
    marginBottom: 20,
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 3,
  },
  formTitle: {
    fontSize: 18,
    fontWeight: "bold",
    marginBottom: 4,
  },
  formSubtitle: {
    fontSize: 14,
    marginBottom: 16,
  },
  input: {
    padding: 12,
    borderRadius: 8,
    marginBottom: 16,
    fontSize: 16,
  },
  checkItem: {
    padding: 12,
    borderRadius: 8,
    marginBottom: 8,
  },
  checkItemHeader: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 8,
  },
  checkItemName: {
    fontSize: 15,
    fontWeight: "600",
  },
  checkItemExpected: {
    fontSize: 13,
  },
  checkItemInput: {
    flexDirection: "row",
    alignItems: "center",
  },
  quantityInput: {
    width: 80,
    padding: 8,
    borderRadius: 6,
    textAlign: "center",
    fontSize: 16,
    fontWeight: "bold",
  },
  diffText: {
    marginLeft: 12,
    fontWeight: "bold",
    fontSize: 15,
  },
  saveButton: {
    padding: 14,
    borderRadius: 8,
    alignItems: "center",
    marginTop: 16,
  },
  saveButtonText: {
    color: "#fff",
    fontWeight: "bold",
    fontSize: 16,
  },
  cpCard: {
    padding: 16,
    borderRadius: 12,
    marginBottom: 12,
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.08,
    shadowRadius: 2,
    elevation: 2,
  },
  cpHeader: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "flex-start",
  },
  cpDate: {
    fontSize: 17,
    fontWeight: "bold",
  },
  cpMeta: {
    fontSize: 13,
    marginTop: 2,
  },
  cpNotes: {
    fontSize: 13,
    fontStyle: "italic",
    marginTop: 8,
  },
  cpItem: {
    paddingVertical: 8,
    paddingHorizontal: 12,
    marginTop: 8,
    borderRadius: 6,
  },
  cpItemName: {
    fontSize: 14,
    fontWeight: "600",
    marginBottom: 4,
  },
  cpItemRow: {
    flexDirection: "row",
    gap: 16,
    flexWrap: "wrap",
  },
  deleteSmall: {
    backgroundColor: "#dc3545",
    paddingHorizontal: 10,
    paddingVertical: 4,
    borderRadius: 6,
  },
  deleteSmallText: {
    color: "#fff",
    fontSize: 12,
    fontWeight: "600",
  },
  emptyText: {
    textAlign: "center",
    marginTop: 40,
    fontSize: 16,
  },
});
